import * as React from "react";
import "./reconciliationModal.scss"
import Transaction from "../../../../../model/transaction/Transaction";
import Modal from "../../../../ui/modal/Modal";
import ButtonPrimary from "../../../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import {none, option, Option} from "ts-option";
import {api} from "../../../../../api/api";
import {notifyBugsnagAndShowToast} from "../../../../../utils/Utils";
import {ReconciliationSuccess} from "./ReconciliationSuccess";
import {Recommendations} from "./Recommendations";
import {RouteComponentProps} from "react-router";
import Toast from "../../../../helper/Toast";
import {PaymentDetails} from "./PaymentDetails";
import {AmountToReconcile} from "./AmountToReconcile";
import {ConfirmReconcilePopup} from "./ConfirmReconcilePopup";
import {SelectedSummaryInfo} from "./SelectedSummaryInfo";
import {ReconciliationModalNavigation} from "./ReconciliationModalNavigation";
import BillSummary from "../../../../../model/bill/BillSummary";
import BillPaymentSummary from "../../../../../model/bill/BillPaymentSummary";
import BillSelectionComponent from "./selectBill/BillSelectionComponent";
import {BillInfo} from "./BillInfo";
import {BillPaymentSummariesTable} from "./BillPaymentSummariesTable";
import Routes from "../../../../../navigation/route/Route";
import {connect, Dispatch} from "react-redux";
import {withRouter} from "react-router-dom";
import {fetchBill} from "../../../../../store/action/bill/bill";
import classNames = require("classnames");

interface IProps {
	/**
	 * Transaction to be reconciled.
	 */
	transaction: Transaction;
	/**
	 * On close callback
	 */
	onClose: () => void;
	/**
	 * Successful reconciliation callback
	 */
	onReconciliationSuccess: () => void;
	/**
	 * Read only indicator.
	 */
	readOnly?: boolean;
	/**
	 * If true, internal navigation between transactions is visible.
	 */
	showNavigation?: boolean;
	/**
	 * Left navigation button click callback.
	 */
	onNavigateLeft?: () => void;
	/**
	 * Right navigation button click callback.
	 */
	onNavigateRight?: () => void;
	/**
	 * If true, left navigation button is disabled.
	 */
	navigateLeftDisabled?: boolean;
	/**
	 * If true, right navigation button is disabled.
	 */
	navigateRightDisabled?: boolean;
	/**
	 * Text to be rendered next to navigation buttons.
	 */
	navigationLabel?: string;
}

interface DispatchProps {
	/**
	 * Fetch bill action.
	 * @param {string} billId bill identifier.
	 */
	fetchBill: (billId: string) => void;
}

interface IState {
	/**
	 * Bill selected for reconciliation.
	 */
	selectedBill: Option<BillSummary>;
	/**
	 * Bills recommended to be reconciliationStatus with transaction from props.
	 */
	recommendedBills: BillSummary[];
	/**
	 * Index of shown recommended bill.
	 */
	recommendedBillIndex: number;
	/**
	 * Indicates if recommended bills are shown.
	 */
	showRecommended: boolean;
	/**
	 * If true, show reconciliation success info.
	 */
	reconciliationSuccess: boolean;
	/**
	 * Bill payments summary data.
	 */
	billPaymentSummaries: BillPaymentSummary[];
	/**
	 * Summary selected to be displayed.
	 */
	selectedSummary: Option<BillPaymentSummary>;
	/**
	 * If true, confirm undo reconciliation popup is displayed.
	 */
	showUndoReconciliationConfirm: boolean;
	/**
	 * Indicating if request to server is in progress.
	 */
	isLoading: boolean;
}

type Props = IProps & DispatchProps & RouteComponentProps<{}>;

class BillReconciliationModal extends React.Component<Props, IState> {

	constructor(props: Props) {
		super(props);
		this.state = {
			selectedBill: none,
			recommendedBills: [],
			showRecommended: true,
			recommendedBillIndex: 0,
			reconciliationSuccess: props.readOnly,
			billPaymentSummaries: [],
			selectedSummary: none,
			showUndoReconciliationConfirm: false,
			isLoading: false
		};
		this.renderBillMatchComponent = this.renderBillMatchComponent.bind(this);
	}

	componentDidMount() {
		this.fetchRecommendations();
		this.fetchBillPaymentSummmaries();
	}

	fetchRecommendations = () => api.instance.transactionApi.fetchBillRecommendationsForTransaction(this.props.transaction.id)
		.then(recommendedBills => {
			const showRecommended = !!recommendedBills.length;
			this.setState({recommendedBills, showRecommended});
		})
		.catch(error => notifyBugsnagAndShowToast(error, "Error fetching recommended bills"));

	render() {
		const classFromProps = classNames({
			"c-reconciliation-modal_invoice-container": true,
			"readonly": this.props.readOnly && this.state.selectedSummary.isEmpty
		});
		const transaction = this.props.transaction;
		const modalLabel = this.props.readOnly ? "Reconciliation Details" : "Reconcile payment to Bill";
		return (
			<Modal onCloseClicked={this.props.onClose} title={modalLabel} autoWidth={true}
				   customClass="c-reconciliation-modal">
				<div className="c-reconciliation-modal_content-container">
					<PaymentDetails transaction={transaction}
									showMatchRemainingAmount={!this.props.readOnly && this.state.selectedSummary.isDefined}
									onMatchRemainingAmountClick={this.onMatchRemainingAmountClick}/>
					<div className={classFromProps}>{this.renderBillMatchComponent()}</div>
					{(!this.state.reconciliationSuccess || this.state.showUndoReconciliationConfirm) && !this.state.isLoading &&
					this.renderReconcilePopup()}
				</div>
				<BillPaymentSummariesTable billPaymentSummaries={this.state.billPaymentSummaries}
										   selectedSummaryId={this.state.selectedSummary.map(s => s.id)}
										   onSummarySelect={this.onSummarySelect}/>
				<AmountToReconcile due={this.props.transaction.due}/>
				<div className="c-reconciliation-modal_navigation-container">
					<div className="c-reconciliation-modal_navigation-container_navigator">
						{/*TODO: to be handled in workspace */}
						<ReconciliationModalNavigation visible={this.props.showNavigation}
													   navigateLeftDisabled={this.props.navigateLeftDisabled}
													   navigateRightDisabled={this.props.navigateRightDisabled}
													   onNavigateLeft={this.onNavigateLeft}
													   onNavigateRight={this.onNavigateRight}
													   navigationLabel={this.props.navigationLabel}/>
					</div>
					<div className="c-reconciliation-modal_navigation-container_button">
						<ButtonPrimary label={"Done"} onClick={this.props.onClose}/>
					</div>
				</div>
			</Modal>
		);
	}

	onMatchRemainingAmountClick = () => this.setState({
		reconciliationSuccess: false,
		selectedSummary: none,
		selectedBill: none,
		showUndoReconciliationConfirm: false,
		showRecommended: true
	});

	onSummarySelect = (summary: BillPaymentSummary) => this.setState({
		selectedSummary: option(summary),
		selectedBill: none,
		showUndoReconciliationConfirm: false,
		showRecommended: false
	});

	onNavigateLeft = () => this.setState({selectedSummary: none}, this.props.onNavigateLeft);

	onNavigateRight = () => this.setState({selectedSummary: none}, this.props.onNavigateRight);


	redirectToBill = () => this.state.selectedBill.map(bill => this.props.history.push(Routes.BillDetailsView(bill.id)));

	renderBillMatchComponent() {
		const renderRecommendations = this.state.showRecommended && this.state.recommendedBills.length;
		if (this.state.selectedSummary.isDefined) {
			return (
				<SelectedSummaryInfo billSummary>
					{this.state.selectedSummary.map(this.renderBillInfo).orNull}
				</SelectedSummaryInfo>
			);
		}
		if (this.state.reconciliationSuccess) {
			return (
				<ReconciliationSuccess due={this.props.transaction.due}
									   referenceNumber={this.state.selectedBill.map(
										   bill => bill.referenceNumber)}
									   onMatchRemainingAmount={() => this.setState(
										   {reconciliationSuccess: false, selectedBill: none}, this.fetchRecommendations)}
									   readOnly={this.props.readOnly}
									   onReferenceNumberClick={this.redirectToBill}
									   onUndoReconciliationClick={() => this.setState({showUndoReconciliationConfirm: true})}/>
			);
		}
		if (renderRecommendations) {
			return (
				<Recommendations recommendationsLength={this.state.recommendedBills.length}
								 onPreviousRecommendationClick={() => this.setState(
									 {recommendedBillIndex: this.state.recommendedBillIndex - 1})}
								 onNextRecommendationClick={() => this.setState(
									 {recommendedBillIndex: this.state.recommendedBillIndex + 1})}
								 recommendationIndex={this.state.recommendedBillIndex}>
					{this.renderBillInfo(this.state.recommendedBills[this.state.recommendedBillIndex])}
				</Recommendations>
			);
		}
		return (
			<>
				<div className="c-reconciliation-modal_details-title">Match bill</div>
				{this.state.selectedBill.map(this.renderBillInfo).getOrElse(() =>
					<>
						<BillSelectionComponent
							onSelect={(bill) => this.setState({selectedBill: option(bill)})}/>
						<div className="c-reconciliation-modal_details-subtitle">Find the correct bill for the
							transaction on the left.
						</div>
					</>)}
			</>
		);
	}

	renderBillInfo = (bill: BillSummary | BillPaymentSummary) => {
		return (
			<BillInfo bill={bill}
					  showUndoReconciliation={this.state.selectedSummary.map(s => s.canUndoReconciliation).getOrElse(false)}
					  onUndoReconciliationClick={() => this.setState({showUndoReconciliationConfirm: true})}/>
		);
	};

	renderReconcilePopup = () => {
		let billToReconcile: Option<BillSummary>;
		let question: string = "Is this the right bill for the transaction?";
		let noOptionLabel: string;
		let noOptionCallback: () => void;
		let yesOptionLabel: string = "Yes, reconcile transaction";
		let yesOptionCallback: () => void = () => this.onReconcile(billToReconcile);
		if (this.state.showUndoReconciliationConfirm) {
			const billId: Option<string> = this.state.selectedSummary.map(s => option(s.billId)).getOrElse(
				this.state.selectedBill.map(i => i.id));
			question = "Are you sure you want to unreconcile?";
			noOptionLabel = "Cancel";
			noOptionCallback = () => this.setState({showUndoReconciliationConfirm: false});
			yesOptionLabel = "Yes, unreconcile this bill";
			yesOptionCallback = () => billId.map(this.onUndoReconciliation);
		}
		else if (this.state.showRecommended && this.state.recommendedBills.length) {
			billToReconcile = option(this.state.recommendedBills[this.state.recommendedBillIndex]);
			noOptionLabel = "No, search manually";
			noOptionCallback = () => this.setState({showRecommended: false});
		}
		else if (this.state.selectedBill.isDefined) {
			billToReconcile = this.state.selectedBill;
			noOptionLabel = "No, search again";
			noOptionCallback = () => this.setState({selectedBill: none});
		}
		else {
			return null;
		}
		return (
			<ConfirmReconcilePopup question={question}
								   yesOptionLabel={yesOptionLabel}
								   noOptionLabel={noOptionLabel}
								   yesOptionCallback={yesOptionCallback}
								   noOptionCallback={noOptionCallback}/>
		);
	};

	onReconcile = (bill: Option<BillSummary>) => this.setState({isLoading: true},
		() => bill.map(b =>
			api.instance.transactionApi.reconcileBillTransaction(this.props.transaction.id, b.id)
				.then(() => {
					this.props.onReconciliationSuccess();
					Toast.success("Transaction reconciled successfully", "Transaction reconciliation");
					this.props.fetchBill(b.id);
					this.setState({reconciliationSuccess: true, selectedBill: bill, isLoading: false},
						() => {
							this.fetchBillPaymentSummmaries();
							this.fetchRecommendations();
						});
				})
				.catch(error => {
					this.setState({isLoading: false});
					notifyBugsnagAndShowToast(error, "Reconcile transaction");
				})
		)
	);

	onUndoReconciliation = (billId: string) => this.setState({isLoading: true},
		() => api.instance.transactionApi.undoReconcileBillTransaction(this.props.transaction.id, billId)
			.then(() => {
				this.props.onReconciliationSuccess();
				Toast.success("Transaction unreconciled successfully", "Undo reconciliation");
				this.props.fetchBill(billId);
				this.setState({
						reconciliationSuccess: false,
						selectedSummary: none,
						selectedBill: none,
						showUndoReconciliationConfirm: false,
						isLoading: false
					},
					() => {
						this.fetchBillPaymentSummmaries();
						this.fetchRecommendations();
					})
			})
			.catch(error => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(error, "Undo reconciliation")
			}));

	fetchBillPaymentSummmaries = () =>
		api.instance.transactionApi.fetchBillPaymentSummariesForTransaction(this.props.transaction.id)
			.then((billPaymentSummaries) => this.setState({billPaymentSummaries}))
			.catch((error) => notifyBugsnagAndShowToast(error, "Fetch bill payments summary"));
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchBill: (billId: string) => dispatch(
			fetchBill(billId, () => {}, (error) => notifyBugsnagAndShowToast(error, "Fetch bill"))
		)
	}
}

export default withRouter<IProps>(connect(null, mapDispatchToProps)(BillReconciliationModal))